import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, ShieldCheck, KeyRound, Lock, CheckCircle2 } from "lucide-react";
import Navbar from "../components/Navbar";
import api, { formatApiErrorDetail } from "../api/client";

export default function Vault() {
    const { id } = useParams();
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const nav = useNavigate();
    const [vault, setVault] = useState(null);
    const [err, setErr] = useState("");
    const [polling, setPolling] = useState(!!sessionId);
    const [pollMsg, setPollMsg] = useState("Verifying your payment…");
    const [releasing, setReleasing] = useState(false);

    // Poll payment status if returning from Stripe
    useEffect(() => {
        if (!sessionId) return;
        let attempts = 0;
        let stop = false;
        const tick = async () => {
            if (stop) return;
            attempts += 1;
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setPollMsg("Payment confirmed — unlocking vault…");
                    setPolling(false);
                    loadVault();
                    return;
                }
                if (data.status === "expired") {
                    setPollMsg("Payment session expired.");
                    setPolling(false);
                    return;
                }
            } catch (_) {}
            if (attempts < 10) setTimeout(tick, 2000);
            else {
                setPollMsg("Taking longer than expected. Please refresh in a minute.");
                setPolling(false);
            }
        };
        tick();
        return () => {
            stop = true;
        };
        // eslint-disable-next-line
    }, [sessionId]);

    const loadVault = async () => {
        try {
            const { data } = await api.get(`/vault/${id}`);
            setVault(data);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const releaseEscrow = async () => {
        setReleasing(true);
        try {
            await api.post(`/deals/${id}/release-escrow`);
            await loadVault();
        } catch (e) {
            alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setReleasing(false);
        }
    };

    useEffect(() => {
        if (!sessionId) loadVault();
        // eslint-disable-next-line
    }, []);

    return (
        <div className="min-h-screen bg-ink-300 text-silver-100">
            <Navbar />
            <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-16">
                {polling ? (
                    <div className="glass p-10 rounded-sm text-center">
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
                            className="w-12 h-12 border-2 border-platinum border-t-transparent rounded-full mx-auto mb-6"
                        />
                        <h2 className="font-display text-3xl">{pollMsg}</h2>
                        <p className="text-silver-400 mt-3 text-sm">
                            Stripe is confirming your transfer. This takes a few seconds.
                        </p>
                    </div>
                ) : err ? (
                    <div className="glass p-10 rounded-sm text-center" data-testid="vault-error">
                        <h2 className="font-display text-3xl text-silver-300 mb-3">
                            Vault locked
                        </h2>
                        <p className="text-silver-400 mb-6">{err}</p>
                        <Link to="/catalog" className="text-platinum">
                            Back to catalog
                        </Link>
                    </div>
                ) : vault ? (
                    <motion.div
                        initial="hidden"
                        animate="show"
                        variants={{
                            hidden: {},
                            show: { transition: { staggerChildren: 0.15 } },
                        }}
                    >
                        {/* Dramatic unlock */}
                        <motion.div
                            variants={{
                                hidden: { opacity: 0, y: 20 },
                                show: { opacity: 1, y: 0 },
                            }}
                            transition={{ duration: 0.7 }}
                            className="text-center mb-10"
                        >
                            <div className="inline-block text-[11px] tracking-[0.4em] uppercase text-platinum mb-3">
                                {vault.status === "paid_in_escrow"
                                    ? "Escrow confirmed"
                                    : "Ownership unlocked"}
                            </div>
                            <h1 className="font-display text-5xl md:text-7xl leading-none silver-text">
                                {vault.status === "paid_in_escrow"
                                    ? "Review the master."
                                    : "The film is yours."}
                            </h1>
                        </motion.div>

                        <motion.div
                            variants={{
                                hidden: { opacity: 0, scale: 0.96 },
                                show: { opacity: 1, scale: 1 },
                            }}
                            transition={{ duration: 0.8 }}
                            className="relative glass rounded-sm p-8 md:p-12 vault-glow overflow-hidden"
                            data-testid="vault-card"
                        >
                            <div className="absolute -top-32 -right-32 w-96 h-96 bg-platinum/15 blur-3xl" />
                            <div className="grid md:grid-cols-[220px_1fr] gap-8 items-start relative">
                                {vault.poster_url && (
                                    <img
                                        src={vault.poster_url}
                                        alt={vault.film_title}
                                        className="w-[220px] aspect-[2/3] object-cover rounded-sm border border-platinum/30"
                                    />
                                )}
                                <div className="flex-1">
                                    <div className="text-[11px] tracking-[0.3em] uppercase text-silver-400 mb-1">
                                        {vault.status === "paid_in_escrow"
                                            ? "Funds in escrow"
                                            : "Now owned by you"}
                                    </div>
                                    <h2 className="font-display text-4xl md:text-5xl mb-6">
                                        {vault.film_title}
                                    </h2>

                                    <dl className="grid md:grid-cols-2 gap-4 text-sm text-silver-200 mb-6">
                                        <Info
                                            icon={ShieldCheck}
                                            label="Transfer Record"
                                            value={vault.transfer_record_id?.slice(0, 18) + "…"}
                                            testid="vault-transfer-id"
                                        />
                                        <Info
                                            icon={KeyRound}
                                            label="One-time Token"
                                            value={vault.download_token?.slice(0, 18) + "…"}
                                        />
                                    </dl>

                                    <a
                                        href={vault.download_url}
                                        download
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-2 bg-platinum hover:bg-platinum-200 text-ink-300 font-medium px-6 py-3 rounded-sm uppercase tracking-widest text-xs"
                                        data-testid="vault-download-btn"
                                    >
                                        <Download className="w-4 h-4" />
                                        Download Master
                                    </a>

                                    {vault.status === "paid_in_escrow" && (
                                        <div className="mt-6 p-5 border border-amber-400/30 bg-amber-400/5 rounded-sm">
                                            <div className="flex items-center gap-2 text-amber-300 text-xs uppercase tracking-widest mb-2">
                                                <Lock className="w-4 h-4" /> Escrow hold active
                                            </div>
                                            <p className="text-silver-300 text-sm leading-relaxed mb-4">
                                                Your payment of ${Number(vault.escrow_amount || 0).toLocaleString()} is safely held. After verifying the master downloaded cleanly, release funds to the creator and finalise ownership transfer.
                                            </p>
                                            <button
                                                onClick={releaseEscrow}
                                                disabled={releasing}
                                                className="inline-flex items-center gap-2 bg-platinum hover:bg-platinum-200 disabled:opacity-50 text-ink-300 font-medium px-5 py-2.5 rounded-sm uppercase tracking-widest text-[11px]"
                                                data-testid="vault-release-escrow"
                                            >
                                                <CheckCircle2 className="w-4 h-4" />
                                                {releasing ? "Releasing…" : "Confirm receipt & release funds"}
                                            </button>
                                        </div>
                                    )}

                                    {vault.status === "transfer_complete" && (
                                        <div className="mt-6 space-y-3">
                                            <div className="p-4 border border-emerald-400/30 bg-emerald-400/5 rounded-sm text-emerald-300 text-xs uppercase tracking-widest flex items-center gap-2">
                                                <CheckCircle2 className="w-4 h-4" />
                                                Escrow released — transfer complete.
                                            </div>
                                            <a
                                                href={`/credential/${id}`}
                                                className="inline-flex items-center gap-2 bg-platinum hover:bg-white text-ink-400 px-5 py-3 rounded-sm uppercase tracking-widest text-xs"
                                                data-testid="vault-credential-link"
                                            >
                                                View Ownership Credential
                                            </a>
                                        </div>
                                    )}

                                    <p className="mt-6 text-xs text-silver-500 max-w-md">
                                        This download link is time-limited and audited. Your
                                        signed Purchase Agreement, Chain-of-Title documents and
                                        Transfer Record are archived in perpetuity.
                                    </p>
                                </div>
                            </div>
                        </motion.div>

                        <div className="text-center mt-10">
                            <button
                                onClick={() => nav("/catalog")}
                                className="text-silver-400 hover:text-platinum text-sm uppercase tracking-widest"
                                data-testid="vault-back"
                            >
                                ← Back to catalog
                            </button>
                        </div>
                    </motion.div>
                ) : (
                    <div className="text-silver-500 animate-pulse">Loading vault…</div>
                )}
            </div>
        </div>
    );
}

function Info({ icon: Icon, label, value, testid }) {
    return (
        <div className="flex items-start gap-3" data-testid={testid}>
            <Icon className="w-4 h-4 mt-0.5 text-platinum" />
            <div>
                <div className="text-[10px] tracking-widest uppercase text-silver-500">
                    {label}
                </div>
                <div className="font-mono text-silver-100 text-xs">{value}</div>
            </div>
        </div>
    );
}
